import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '../../contexts/TestAuthContext';
import { FileText, Upload, Loader2, CheckCircle, AlertCircle, DollarSign, TrendingUp, Info, Users } from 'lucide-react';

/**
 * TaxReturnUpload - Parse IRS Form 1065 and Reconcile with P&L
 * 
 * Phase 2 Feature: Tax Return Parser
 * - Upload 1065 PDF
 * - AI extracts data from all schedules and supporting docs
 * - Reconciles with P&L
 * - Suggests adjustments
 * - Calculates claim impact
 */

interface TaxReturnUploadProps {
  onDataParsed?: (taxData: any) => void;
  plData?: any; // P&L data for reconciliation
  demoMode?: boolean; // Use demo endpoint without auth
  existingFiles?: Array<{
    filename: string;
    year: number;
    data: any;
    uploadedAt?: string;
  }>; // Previously uploaded files to display
}

export default function TaxReturnUpload({ onDataParsed, plData, demoMode = false, existingFiles = [] }: TaxReturnUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [taxData, setTaxData] = useState(null);
  const [reconciliation, setReconciliation] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [error, setError] = useState(null);
  const [uploadMessage, setUploadMessage] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  
  // NEW: Track multiple 1065 uploads by year - Initialize with existing files
  const [tax1065Files, setTax1065Files] = useState<Array<{
    filename: string;
    year: number;
    data: any;
    uploadedAt: string;
  }>>(existingFiles);

  // Notify parent whenever tax1065Files changes
  React.useEffect(() => {
    if (onDataParsed && tax1065Files.length > 0) {
      onDataParsed(tax1065Files);
    }
  }, [tax1065Files, onDataParsed]);

  const { getToken } = useAuth();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  
  // NEW: Detect year from 1065 filename or parsed data
  const detectTaxYear = (filename: string, parsedData: any = null) => {
    // Try filename first
    const yearMatch = filename.match(/20\d{2}/);
    if (yearMatch) {
      return parseInt(yearMatch[0]);
    }
    
    // Try parsed tax_year field
    if (parsedData?.tax_year) {
      return parseInt(parsedData.tax_year);
    }
    
    // Default to current year
    return new Date().getFullYear();
  };

  // Upload and parse 1065 - USING XMLHttpRequest to bypass rrweb-recorder
  // NOW SUPPORTS MULTIPLE FILES (4 years of 1065s + 8 K-1s)
  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    console.log(`📤 Uploading ${acceptedFiles.length} file(s)...`);
    setUploading(true);
    
    // Track completed uploads
    let completedUploads = 0;
    const totalUploads = acceptedFiles.length;

    // Process ALL accepted files sequentially
    for (let i = 0; i < acceptedFiles.length; i++) {
      const file = acceptedFiles[i];
      console.log(`   Processing file ${i + 1}/${totalUploads}: ${file.name}`);
    
    // Support multiple file types (v1.4 Enhancement)
    const allowedTypes = [
      'application/pdf',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
      'application/x-csv',
      'text/plain' // For .IIF files
    ];
    
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const allowedExtensions = ['pdf', 'xlsx', 'xls', 'csv', 'iif', 'qbo'];
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension || '')) {
      setError('Please upload a PDF, Excel (.xlsx, .xls), CSV, or QuickBooks (.iif, .qbo) file');
      setUploading(false);
      return;
    }

    // Don't set uploading here - it's already set before the loop
    setError(null);

    const formData = new FormData();
    formData.append('file', file);

    // Use demo endpoint if in demo mode
    const endpoint = demoMode 
      ? `${BACKEND_URL}/api/v1/tax/parse-1065-demo`
      : `${BACKEND_URL}/api/v1/tax/parse-1065`;

    // Get auth token for non-demo mode
    let authToken = null;
    if (!demoMode) {
      try {
        authToken = await getToken();
      } catch (err) {
        console.error('Failed to get auth token:', err);
        setError('Authentication failed. Please sign in again.');
        setUploading(false);
        return;
      }
    }

    // BYPASS rrweb-recorder: Use XMLHttpRequest instead of fetch
    const xhr = new XMLHttpRequest();
    xhr.open('POST', endpoint, true);
    
    // Add auth header for non-demo mode
    if (authToken) {
      xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
    }

    xhr.onload = () => {
      try {
        const result = JSON.parse(xhr.responseText);
        
        if (xhr.status >= 200 && xhr.status < 300) {
          // SUCCESS
          const detectedYear = detectTaxYear(file.name, result.data);
          
          // Add to 1065 files list with year
          const tax1065Entry = {
            filename: file.name,
            year: detectedYear,
            data: result.data,
            uploadedAt: new Date().toISOString()
          };
          
          setTax1065Files(prev => [...prev, tax1065Entry]);
          
          // Also update single tax data for backward compatibility
          setTaxData(result.data);
          setUploadedFile({
            name: file.name,
            size: file.size
          });

          // DEBUG: Log form_type to verify K-1 detection
          console.log(`✅ Tax file uploaded: ${file.name} → Year ${detectedYear}`);
          console.log(`   Form Type: "${result.data.form_type}" (Type: ${typeof result.data.form_type})`);
          console.log(`   Partner Name: ${result.data.partner_name || 'N/A'}`);
          console.log(`   Business Name: ${result.data.business_name || 'N/A'}`);

          // Auto-reconcile if P&L data exists (use last uploaded file)
          if (plData && acceptedFiles[acceptedFiles.length - 1] === file) {
            reconcileData(result.data, plData);
          }
        } else {
          // ERROR
          setError(result.detail || result.message || 'Upload failed');
        }
      } catch (parseError) {
        setError('Invalid response from server');
      }
      
      // Track completed uploads
      completedUploads++;
      console.log(`   Upload ${completedUploads}/${totalUploads} completed`);
      
      // All uploads complete - re-enable upload
      if (completedUploads === totalUploads) {
        setUploading(false);
        console.log(`✅ All ${totalUploads} file(s) uploaded successfully`);
        if (totalUploads > 1) {
          setUploadMessage(`Processed ${totalUploads} files! Check the list below for all uploaded forms.`);
        }
      }
    };

    xhr.onerror = () => {
      setError(`Network error uploading ${file.name}`);
      
      // Track completed uploads (even on error)
      completedUploads++;
      console.log(`   Upload ${completedUploads}/${totalUploads} completed (error)`);
      
      // All uploads complete - re-enable upload
      if (completedUploads === totalUploads) {
        setUploading(false);
      }
    };

    xhr.ontimeout = () => {
      setError(`Upload timed out for ${file.name}`);
      
      // Track completed uploads (even on timeout)
      completedUploads++;
      console.log(`   Upload ${completedUploads}/${totalUploads} completed (timeout)`);
      
      // All uploads complete - re-enable upload
      if (completedUploads === totalUploads) {
        setUploading(false);
      }
    };

    // Set timeout to 60 seconds (Claude can be slow)
    xhr.timeout = 60000;

    xhr.send(formData);
    }  // End of for loop for multiple files
    
    // Note: setUploading(false) is now handled in xhr response handlers
    // after ALL uploads complete, not immediately after loop ends
  }, [BACKEND_URL, plData, onDataParsed, demoMode, getToken, detectTaxYear]);

  // Reconcile tax data with P&L - USING XMLHttpRequest to bypass rrweb-recorder
  const reconcileData = async (tax: any, pl: any) => {
    setReconciling(true);
    setError(null);

    // Get auth token if not in demo mode
    let authToken = null;
    if (!demoMode) {
      try {
        authToken = await getToken();
      } catch (err) {
        console.error('Failed to get auth token for reconciliation:', err);
        setError('Authentication failed. Please sign in again.');
        setReconciling(false);
        return;
      }
    }

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BACKEND_URL}/api/v1/tax/reconcile`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    
    // Add auth header if available
    if (authToken) {
      xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
    }

    xhr.onload = () => {
      try {
        const result = JSON.parse(xhr.responseText);
        
        if (xhr.status >= 200 && xhr.status < 300) {
          // SUCCESS
          setReconciliation(result.reconciliation);
        } else {
          // ERROR
          setError(result.detail || result.message || 'Reconciliation failed');
        }
      } catch (parseError) {
        setError('Invalid response from server');
      } finally {
        setReconciling(false);
      }
    };

    xhr.onerror = () => {
      setError('Network error during reconciliation');
      setReconciling(false);
    };

    xhr.send(JSON.stringify({
      claim_id: `claim-${Date.now()}`,
      tax_data: tax,
      pl_data: pl
    }));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 
      'application/pdf': ['.pdf'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
      'application/x-csv': ['.csv'],
      'text/plain': ['.iif'], // QuickBooks IIF
      'application/vnd.intu.qbo': ['.qbo'] // QuickBooks Online
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    multiple: true  // ✅ ENABLE MULTIPLE 1065 FILES (4 years of tax returns)
  });

  // Approve adjustments
  const handleApprove = () => {
    // TODO: Apply adjustments to claim
    setUploadMessage('Adjustments approved! Claim updated.');
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mb-6 border-l-4 border-blue-500">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <FileText className="w-6 h-6 text-blue-600" />
            Upload Form 1065 (Optional - Tax Return)
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Upload complete Form 1065 with all schedules (K, L, M, etc.) and supporting financial documents
          </p>
        </div>
        <div className="bg-blue-50 px-3 py-1 rounded-full">
          <span className="text-xs font-semibold text-blue-700">Phase 2 Feature</span>
        </div>
      </div>

      {/* Upload Area - ALWAYS SHOW (not conditioned on taxData) */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          isDragActive
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 hover:border-blue-400'
        }`}
      >
        <input {...getInputProps()} />
        {uploading ? (
          <div className="flex flex-col items-center">
            <Loader2 className="w-10 h-10 text-blue-600 animate-spin mb-3" />
            <p className="font-semibold">Parsing Form 1065...</p>
            <p className="text-xs text-gray-500 mt-1">Extracting Schedule K data</p>
          </div>
        ) : (
            <div className="flex flex-col items-center">
              <Upload className="w-10 h-10 text-gray-400 mb-2" />
              <p className="font-semibold mb-1">
                {isDragActive ? 'Drop files here...' : 'Upload Financial Documents'}
              </p>
              <p className="text-xs text-gray-500">
                PDF, Excel (.xlsx, .xls), CSV, QuickBooks (.iif, .qbo)
              </p>
            </div>
          )}
      </div>

      {/* K-1 Info Banner */}
      <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-semibold text-blue-900 mb-1">💡 K-1 Schedules (Officer Compensation)</p>
          <p className="text-blue-800">
            For accurate BI calculations, include Schedule K-1 forms showing Box 1 (partner income). 
            If K-1s are not included, the system will use payroll data as a fallback for officer compensation.
          </p>
          <ul className="mt-2 text-blue-700 space-y-1 ml-4 list-disc">
            <li>Upload complete 1065 PDFs including K-1 schedules for best results</li>
            <li>Or upload K-1 schedules separately as additional files</li>
            <li>System auto-detects and uses payroll when K-1s are missing</li>
          </ul>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-red-800">Error</p>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}
      
      {/* NEW: Display uploaded 1065 files by year */}
      {tax1065Files.length > 0 && (
        <div className="mt-6 border-t pt-6">
          <h3 className="font-semibold text-gray-900 mb-3">📋 Uploaded 1065 Forms ({tax1065Files.length})</h3>
          <div className="grid grid-cols-1 gap-3">
            {tax1065Files
              .sort((a, b) => a.year - b.year)  // Sort by year
              .map((taxFile, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-gray-900">{taxFile.filename}</p>
                      <p className="text-xs text-gray-600">
                        Tax Year: <span className="font-semibold">{taxFile.year}</span>
                        {taxFile.data?.business_name && ` • ${taxFile.data.business_name}`}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setTax1065Files(prev => prev.filter((_, i) => i !== index))}
                    className="text-red-600 hover:text-red-800 text-sm font-medium px-3 py-1 rounded hover:bg-red-100"
                  >
                    Remove
                  </button>
                </div>
              ))}
          </div>
          
          {/* Year coverage indicator */}
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Tax years covered:</strong> {[...new Set(tax1065Files.map(f => f.year))].sort().join(', ')}
            </p>
            {tax1065Files.length < 4 && (
              <p className="text-xs text-orange-700 mt-1">
                ⚠️ Note: BI analysis requires 4 years of data (current year + 3 prior years)
              </p>
            )}
          </div>
          
          {/* 1065 FORM MATRIX - Multi-year view of all 1065 uploads */}
          {(() => {
            const form1065Files = tax1065Files.filter(f => f.data?.form_type === '1065');
            if (form1065Files.length === 0) return null;
            
            // Sort by year
            const sortedForms = form1065Files.sort((a, b) => a.year - b.year);
            const years = sortedForms.map(f => f.year);
            
            return (
              <div className="mt-6 border-t pt-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Form 1065 Summary - Multi-Year View ({form1065Files.length} years)
                </h3>
                
                <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left px-4 py-3 font-semibold text-gray-700">Schedule K Line Item</th>
                        {years.map(year => (
                          <th key={year} className="text-right px-4 py-3 font-semibold text-gray-700">
                            {year}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {/* Line 1: Ordinary Income */}
                      <tr className="bg-blue-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">Line 1: Ordinary Income</p>
                          <p className="text-xs text-gray-600">Business income (loss)</p>
                        </td>
                        {sortedForms.map(form => (
                          <td key={form.year} className="text-right px-4 py-3 font-mono font-semibold text-blue-900">
                            ${form.data?.schedule_k?.line_1_ordinary_income?.toLocaleString() || '0'}
                          </td>
                        ))}
                      </tr>
                      
                      {/* Line 9: Salaries & Wages */}
                      <tr className="bg-blue-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">Line 9: Salaries & Wages</p>
                          <p className="text-xs text-gray-600">Total payroll baseline</p>
                        </td>
                        {sortedForms.map(form => (
                          <td key={form.year} className="text-right px-4 py-3 font-mono font-semibold text-blue-900">
                            ${form.data?.schedule_k?.line_9_salaries_wages?.toLocaleString() || '0'}
                          </td>
                        ))}
                      </tr>
                      
                      {/* Line 13d: Other Deductions */}
                      <tr>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">Line 13d: Other Deductions</p>
                          <p className="text-xs text-gray-600">Miscellaneous expenses</p>
                        </td>
                        {sortedForms.map(form => (
                          <td key={form.year} className="text-right px-4 py-3 font-mono text-gray-700">
                            ${form.data?.schedule_k?.line_13d_other_deductions?.toLocaleString() || '0'}
                          </td>
                        ))}
                      </tr>
                      
                      {/* Line 22: Guaranteed Payments */}
                      <tr className="bg-green-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">Line 22: Guaranteed Payments</p>
                          <p className="text-xs text-gray-600">Officer compensation (B2T)</p>
                        </td>
                        {sortedForms.map(form => (
                          <td key={form.year} className="text-right px-4 py-3 font-mono font-semibold text-green-900">
                            ${form.data?.schedule_k?.line_22_guaranteed_payments?.toLocaleString() || '0'}
                          </td>
                        ))}
                      </tr>
                      
                      {/* Gross Receipts */}
                      <tr className="bg-yellow-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">Gross Receipts (Line 1c)</p>
                          <p className="text-xs text-gray-600">Total revenue</p>
                        </td>
                        {sortedForms.map(form => (
                          <td key={form.year} className="text-right px-4 py-3 font-mono font-semibold text-yellow-900">
                            ${form.data?.gross_receipts?.toLocaleString() || '0'}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                {/* Business Info */}
                {sortedForms[0]?.data?.business_name && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold">Business:</span> {sortedForms[0].data.business_name}
                      {sortedForms[0].data.ein && ` • EIN: ${sortedForms[0].data.ein}`}
                    </p>
                  </div>
                )}
              </div>
            );
          })()}
          
          {/* K-1 PARTNER MATRIX - Aggregated view from 1065s and K-1s */}
          {(() => {
            // Aggregate K-1 data from both standalone K-1 files AND from 1065 forms (which include K-1 data)
            const filesWithK1Data = tax1065Files.filter(f => {
              // Include standalone K-1 files
              if (f.data?.form_type === 'K-1') return true;
              // Include 1065 files that have K-1 partner data
              if (f.data?.form_type === '1065' && f.data?.schedule_k1?.partners) return true;
              return false;
            });
            if (filesWithK1Data.length === 0) return null;
            
            const partnerData: Record<string, Record<string, number>> = {};
            const yearTotals: Record<string, number> = {};
            
            filesWithK1Data.forEach(file => {
              const data = file.data;
              const year = String(data.tax_year || file.year);
              
              // Check if this is a 1065 with embedded K-1 partners data
              if (data.form_type === '1065' && data.schedule_k1?.partners && Array.isArray(data.schedule_k1.partners)) {
                // Extract each partner from the 1065's K-1 data
                data.schedule_k1.partners.forEach((partner: any) => {
                  const partnerName = partner.name || 'Unknown Partner';
                  const box1Amount = partner.box_1_ordinary_income || 0;
                  
                  if (!partnerData[partnerName]) partnerData[partnerName] = {};
                  partnerData[partnerName][year] = (partnerData[partnerName][year] || 0) + box1Amount;
                  yearTotals[year] = (yearTotals[year] || 0) + box1Amount;
                });
              } else {
                // Standalone K-1 file
                const partnerName = data.partner_name || 'Unknown Partner';
                const box1Amount = data.schedule_k1?.total_officer_compensation || 0;
                
                if (!partnerData[partnerName]) partnerData[partnerName] = {};
                partnerData[partnerName][year] = box1Amount;
                yearTotals[year] = (yearTotals[year] || 0) + box1Amount;
              }
            });
            
            const partners = Object.keys(partnerData);
            const years = Object.keys(yearTotals).sort();
            const grandTotal = Object.values(yearTotals).reduce((sum, val) => sum + val, 0);
            
            return (
              <div className="mt-6 border-t pt-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  K-1 Partner Summary ({partners.length} partner{partners.length !== 1 ? 's' : ''} • {years.length} year{years.length !== 1 ? 's' : ''})
                </h3>
                
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left px-4 py-3 font-semibold text-gray-700">Partner</th>
                        {years.map(year => (
                          <th key={year} className="text-right px-4 py-3 font-semibold text-gray-700">
                            {year}
                          </th>
                        ))}
                        <th className="text-right px-4 py-3 font-semibold text-blue-700 bg-blue-50">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {partners.map(partner => {
                        const partnerTotal = Object.values(partnerData[partner]).reduce((sum, val) => sum + val, 0);
                        return (
                          <tr key={partner} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium text-gray-900">{partner}</td>
                            {years.map(year => (
                              <td key={year} className="text-right px-4 py-3 text-gray-700">
                                {partnerData[partner][year] 
                                  ? `$${partnerData[partner][year].toLocaleString()}`
                                  : '-'}
                              </td>
                            ))}
                            <td className="text-right px-4 py-3 font-semibold text-blue-700 bg-blue-50">
                              ${partnerTotal.toLocaleString()}
                            </td>
                          </tr>
                        );
                      })}
                      
                      <tr className="bg-blue-50 font-bold">
                        <td className="px-4 py-3 text-blue-900">Total</td>
                        {years.map(year => (
                          <td key={year} className="text-right px-4 py-3 text-blue-900">
                            ${yearTotals[year]?.toLocaleString() || '0'}
                          </td>
                        ))}
                        <td className="text-right px-4 py-3 text-blue-900 bg-blue-100 text-lg">
                          ${grandTotal.toLocaleString()}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Parsed Tax Data */}
      {taxData && (
        <div className="mt-4 space-y-4">
          {/* DEBUG: Form Type Indicator */}
          <div className={`border-2 p-3 rounded-lg ${String(taxData.form_type).trim() === 'K-1' ? 'bg-blue-100 border-blue-500' : 'bg-blue-100 border-blue-500'}`}>
            <p className="font-bold text-lg">
              🔍 DEBUG: Detected Form Type = "{taxData.form_type}"
            </p>
            <p className="text-sm">
              Trimmed: "{String(taxData.form_type).trim()}" | 
              Matches K-1: {String(taxData.form_type).trim() === 'K-1' ? '✅ YES' : '❌ NO'}
            </p>
          </div>
          
          {/* File Info */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div className="flex-1">
                <p className="font-semibold text-green-800">{uploadedFile?.name}</p>
                <p className="text-xs text-green-700">
                  Successfully parsed {taxData.form_type === 'K-1' ? 'Schedule K-1' : 'Form 1065'}
                  <span className="ml-2 text-gray-500">(Type: {taxData.form_type})</span>
                </p>
              </div>
              {taxData.is_amended && (
                <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded">
                  Amended Return
                </span>
              )}
            </div>
          </div>

          {/* Business Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">Business Name</p>
              <p className="font-semibold">{taxData.business_name}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">Tax Year</p>
              <p className="font-semibold">{taxData.tax_year}</p>
            </div>
          </div>

          {/* K-1 or 1065 Data Display */}
          <div>
            {(() => {
              // DEBUG: Log form_type during rendering
              const formType = taxData.form_type;
              const formTypeStr = String(formType).trim();
              const isK1 = formTypeStr === 'K-1';
              console.log(`🔍 Rendering tax display:`);
              console.log(`   form_type value: "${formType}"`);
              console.log(`   form_type type: ${typeof formType}`);
              console.log(`   form_type trimmed: "${formTypeStr}"`);
              console.log(`   Is K-1 (===): ${formTypeStr === 'K-1'}`);
              console.log(`   Is K-1 (includes): ${formTypeStr.includes('K-1')}`);
              return null;
            })()}
            {String(taxData.form_type).trim() === 'K-1' ? (
              /* K-1 Schedule Display */
              <>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Schedule K-1 - Partner: {taxData.partner_name || 'Unknown'}
                </h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-800 font-semibold mb-2">✅ K-1 Detected - Partner Income Extracted</p>
                  <p className="text-xs text-blue-700">
                    This data will be aggregated with other K-1s in Step 5 → K-1 Partner Summary tab
                  </p>
                </div>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-semibold">Box</th>
                        <th className="text-right px-4 py-3 text-sm font-semibold">Amount</th>
                        <th className="text-center px-4 py-3 text-sm font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr>
                        <td className="px-4 py-3">
                          <p className="font-medium">Box 1: Ordinary Business Income</p>
                          <p className="text-xs text-gray-500">Partner's share of income</p>
                        </td>
                        <td className="text-right px-4 py-3 font-mono font-semibold text-lg">
                          ${taxData.schedule_k1?.total_officer_compensation?.toLocaleString() || '0'}
                        </td>
                        <td className="text-center px-4 py-3">
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">✓ Extracted</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              /* Form 1065 Display */
              <>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Form 1065 - Partnership Return
                </h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-800 font-semibold mb-2">✅ Form 1065 Detected - Key Data Extracted</p>
                  <p className="text-xs text-blue-700">
                    Critical fields for BI B2T calculations and officer compensation analysis
                  </p>
                </div>
                
                {/* Schedule K Critical Lines */}
                <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
                  <div className="bg-gray-100 px-4 py-2">
                    <p className="font-semibold text-sm">Schedule K - Partner's Distributive Share Items</p>
                  </div>
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-semibold">Line Item</th>
                        <th className="text-right px-4 py-3 text-sm font-semibold">Amount</th>
                        <th className="text-center px-4 py-3 text-sm font-semibold">BI Use</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr>
                        <td className="px-4 py-3">
                          <p className="font-medium">Line 1: Ordinary Income</p>
                          <p className="text-xs text-gray-500">Business income (loss)</p>
                        </td>
                        <td className="text-right px-4 py-3 font-mono font-semibold text-lg">
                          ${taxData.schedule_k?.line_1_ordinary_income?.toLocaleString() || '0'}
                        </td>
                        <td className="text-center px-4 py-3">
                          {taxData.has_negative_income ? (
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">Loss</span>
                          ) : (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Revenue Check</span>
                          )}
                        </td>
                      </tr>
                      <tr className="bg-blue-50">
                        <td className="px-4 py-3">
                          <p className="font-medium">Line 9: Salaries & Wages</p>
                          <p className="text-xs text-gray-500">Total payroll - Fixed baseline</p>
                        </td>
                        <td className="text-right px-4 py-3 font-mono font-semibold text-lg">
                          ${taxData.schedule_k?.line_9_salaries_wages?.toLocaleString() || taxData.total_payroll?.toLocaleString() || '0'}
                        </td>
                        <td className="text-center px-4 py-3">
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-semibold">Payroll Base</span>
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3">
                          <p className="font-medium">Line 13d: Other Deductions</p>
                          <p className="text-xs text-gray-500">Miscellaneous expenses</p>
                        </td>
                        <td className="text-right px-4 py-3 font-mono font-semibold">
                          ${taxData.schedule_k?.line_13d_other_deductions?.toLocaleString() || '0'}
                        </td>
                        <td className="text-center px-4 py-3">
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Deduction</span>
                        </td>
                      </tr>
                      <tr className="bg-green-50">
                        <td className="px-4 py-3">
                          <p className="font-medium">Line 22: Guaranteed Payments</p>
                          <p className="text-xs text-gray-500">Partner compensation - Critical for B2T</p>
                        </td>
                        <td className="text-right px-4 py-3 font-mono font-semibold text-lg">
                          ${taxData.schedule_k?.line_22_guaranteed_payments?.toLocaleString() || taxData.schedule_k1?.total_officer_compensation?.toLocaleString() || '0'}
                        </td>
                        <td className="text-center px-4 py-3">
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded font-semibold">B2T &lt;5%</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                {/* Officer Compensation Summary */}
                {taxData.schedule_k1?.total_officer_compensation && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="font-semibold text-blue-900 mb-2">Officer Compensation Summary</p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-blue-700">Total Officer Comp (K-1s)</p>
                        <p className="text-2xl font-bold text-blue-900">
                          ${taxData.schedule_k1.total_officer_compensation.toLocaleString()}
                        </p>
                      </div>
                      {taxData.schedule_k1.partners && taxData.schedule_k1.partners.length > 0 && (
                        <div>
                          <p className="text-blue-700">Partners</p>
                          <p className="text-2xl font-bold text-blue-900">
                            {taxData.schedule_k1.partners.length}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Reconciliation Loading */}
          {reconciling && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
              <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
              <p className="text-sm text-blue-800">Reconciling with P&L data...</p>
            </div>
          )}

          {/* Reconciliation Results */}
          {reconciliation && (
            <div className="space-y-4">
              <div className="border-t border-gray-200 pt-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  Reconciliation Results
                </h3>

                {/* Claim Impact */}
                {reconciliation.claim_impact > 0 && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-700 mb-1">Potential Claim Increase</p>
                        <p className="text-3xl font-bold text-green-700 flex items-center gap-2">
                          <DollarSign className="w-8 h-8" />
                          {reconciliation.claim_impact.toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-600">Confidence</p>
                        <p className="text-lg font-bold text-gray-800">
                          {((reconciliation.confidence || 0.95) * 100).toFixed(0)}%
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Discrepancies */}
                {reconciliation.discrepancies && reconciliation.discrepancies.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                      <Info className="w-4 h-4 text-orange-600" />
                      Identified Discrepancies
                    </h4>
                    <div className="space-y-2">
                      {reconciliation.discrepancies.map((disc, idx) => (
                        <div key={idx} className="bg-orange-50 border border-orange-200 rounded p-3 text-sm">
                          <div className="flex justify-between mb-1">
                            <span className="font-medium">{disc.item}</span>
                            <span className="font-mono font-semibold text-orange-700">
                              ${Math.abs(disc.variance).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-xs text-gray-700">{disc.explanation}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Suggested Adjustments */}
                {reconciliation.suggested_adjustments && reconciliation.suggested_adjustments.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2">Suggested Adjustments</h4>
                    <div className="space-y-2 mb-4">
                      {reconciliation.suggested_adjustments.map((adj, idx) => (
                        <div key={idx} className="bg-blue-50 border border-blue-200 rounded p-3">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-medium text-sm">{adj.category}</p>
                              <p className="text-xs text-gray-600 mt-1">{adj.reason}</p>
                            </div>
                            <span className="font-mono font-bold text-blue-700">
                              +${adj.amount.toLocaleString()}
                            </span>
                          </div>
                          {adj.tax_line && (
                            <p className="text-xs text-gray-500 italic">From: {adj.tax_line}</p>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Approve Button */}
                    <button
                      onClick={handleApprove}
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <CheckCircle className="w-5 h-5" />
                      Approve Adjustments & Update Claim
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Manual Reconcile Button */}
          {taxData && !reconciliation && !reconciling && plData && (
            <button
              onClick={() => reconcileData(taxData, plData)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors"
            >
              Reconcile with P&L Data
            </button>
          )}
        </div>
      )}

      {/* Info Box */}
      <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-blue-800">
            <strong>How it works:</strong> Upload your partnership/S-Corp tax return (Form 1065). 
            AI extracts data from Form 1065 (all schedules: K, L, M, balance sheet, P&L), compares with your financial records, and suggests legitimate 
            adjustments to maximize your claim.
          </p>
        </div>
      </div>
    </div>
  );
}
